import { type ReactNode } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import { useSimpleAdminAuth } from '@/hooks/useSimpleAdminAuth'
import { AdminLoginForm } from '@/components/admin/AdminLoginForm'
import { Loader2 } from 'lucide-react'

interface RequireSimpleAdminAuthProps {
  children: ReactNode
}

export function RequireSimpleAdminAuth({ children }: RequireSimpleAdminAuthProps) {
  const { secretToken } = useParams<{ secretToken: string }>()
  const { isAuthenticated, isLoading, validateSecretToken } = useSimpleAdminAuth()

  // Show loading spinner while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
          <p className="text-slate-400 text-sm">Verifying access...</p>
        </div>
      </div>
    )
  }

  // Validate secret token in URL
  if (!secretToken || !validateSecretToken(secretToken)) {
    // Invalid or missing secret token - show 404-like page
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-slate-700">404</h1>
          <p className="mt-4 text-slate-500">Page not found</p>
        </div>
      </div>
    )
  }

  // Valid secret token but not authenticated - show login form
  if (!isAuthenticated) {
    return <AdminLoginForm />
  }

  // Authenticated - render children
  return <>{children}</>
}
